import { t } from '../i18n.js';
import { illustration, illustrationForPath } from './illustration.js';

/**
 * The one non-content state renderer.
 *
 * Twelve page files carried a byte-identical error card and thirteen carried
 * the same tinted-circle empty state, while five screens quietly diverged —
 * `/timers` most dangerously, where a failed load set the collection to `[]`
 * and fell through to the empty branch, so a server error was indistinguishable
 * from "you have no timers" and users created duplicates.
 *
 * Four states, one contract:
 *
 *   loading   skeleton rows
 *   error     message + Retry that re-issues the failed request
 *   empty     first-run zero state: illustration slot, headline, subtitle,
 *             capability-gated CTA
 *   filtered  zero *matches*, which is a different thing from zero *data* —
 *             its own copy and a clear-filters action, never a create CTA
 *
 * An error is never rendered as an empty state. That is the rule the
 * `/timers` defect broke, so it is expressed here rather than left to callers.
 *
 * The `empty` variant renders artwork inside a tinted circle. Callers may name
 * it with `illustrationId`; otherwise it is resolved from the current route, so
 * a new screen inherits a picture without a per-page edit. Passing `iconFn` and
 * no id renders that icon instead, at the smaller inline size. There is deliberately no second
 * empty-state component — that abstraction would have covered one of these four
 * states and cost a second refactor of the same eleven pages.
 */

/** @typedef {'loading'|'error'|'empty'|'filtered'} StateKind */

function shell(kind) {
  const el = document.createElement('div');
  el.dataset.role = 'state-card';
  el.dataset.stateKind = kind;
  return el;
}

/**
 * Skeleton placeholders. Count is a hint, not a promise — callers pass roughly
 * what the real list will show so the page does not jump on load.
 *
 * `variant` matches the skeleton to the content shape. Stacked rows inside a
 * tile grid collapse to one narrow column, which is worse than no skeleton at
 * all: it promises the wrong layout.
 *
 * @param {{ rows?: number, variant?: 'rows'|'tiles' }} [opts]
 * @returns {HTMLElement}
 */
export function stateLoading({ rows = 3, variant = 'rows' } = {}) {
  const wrap = shell('loading');
  wrap.setAttribute('aria-busy', 'true');
  wrap.dataset.stateVariant = variant;

  if (variant === 'tiles') {
    wrap.className = 'grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 animate-pulse';
    for (let i = 0; i < rows; i++) {
      const tile = document.createElement('div');
      // Mirrors the real tile: square, same radius, same touch floor.
      tile.className = 'card aspect-square min-h-[88px] w-full';
      wrap.append(tile);
    }
    return wrap;
  }

  wrap.className = 'space-y-4 animate-pulse';
  for (let i = 0; i < rows; i++) {
    const card = document.createElement('div');
    card.className = 'card space-y-3';
    for (const w of ['w-1/3', 'w-2/3', 'w-1/2']) {
      const line = document.createElement('div');
      // A skeleton line carries no text and no border, so `chip-neutral` (the
      // plate token declared to separate from the card at >=1.18:1) is the
      // fit, not `surface-wash` (a field-fill token that measures 1.04:1
      // against a dark list card and simply disappears).
      line.className = `h-3 bg-chip-neutral dark:bg-chip-neutral-dark rounded ${w}`;
      card.append(line);
    }
    wrap.append(card);
  }
  return wrap;
}

/**
 * Failed load. Always offers a way out — a message with no Retry leaves the
 * user with nothing to do but reload the app.
 *
 * @param {{ messageKey?: string, message?: string, onRetry: () => void }} opts
 * @returns {HTMLElement}
 */
export function stateError({ messageKey = 'errors.server', message, onRetry }) {
  const card = shell('error');
  card.className = 'card text-center space-y-4 mt-2';
  card.setAttribute('role', 'alert');

  const msg = document.createElement('p');
  msg.className = 'text-ink-muted';
  msg.textContent = message || t(messageKey);
  card.append(msg);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.role = 'state-card-retry';
  btn.className = 'btn-primary mx-auto';
  btn.textContent = t('actions.retry');
  btn.addEventListener('click', () => onRetry());
  card.append(btn);

  return card;
}

/**
 * First-run zero state.
 *
 * `canAct` is the capability answer, not a role literal — a member sees the
 * ask-admin subtitle and no CTA, which is the convention every master-data
 * screen already follows.
 *
 * @param {{
 *   illustrationId?: string,
 *   iconFn?: (extraClass?: string) => SVGSVGElement,
 *   titleKey?: string,
 *   title?: string,
 *   subtitleKey?: string,
 *   memberSubtitle?: string,
 *   canAct?: boolean,
 *   actions?: Array<{ labelKey: string, onSelect: () => void, primary?: boolean }>,
 * }} opts
 * @returns {HTMLElement}
 *
 * `memberSubtitle` and `title` take a RESOLVED string, not a key. The permission
 * hint they carry is composed from a capability and a subject
 * (`needsCapabilityText`), so it needs `t()` with a parameter — and a key handed
 * to this component could only ever be resolved without one. Keeping a
 * `*Key` option beside them would be two ways to say one thing, which is the
 * pattern this codebase removes elsewhere (`HUB_PATH`, the `TABS` array).
 */
export function stateEmpty({
  illustrationId,
  iconFn,
  titleKey,
  title: titleText,
  subtitleKey,
  memberSubtitle,
  canAct = true,
  actions = [],
}) {
  const card = shell('empty');
  card.className = 'card text-center mt-2 space-y-4';

  // Scene resolution: an explicit id wins, otherwise the current route decides.
  // A caller that passes only `iconFn` opts out and keeps the tinted circle.
  const sceneId = illustrationId
    || (iconFn ? null : illustrationForPath((globalThis.location?.hash || '#/').slice(1) || '/'));

  const slot = document.createElement('div');
  slot.dataset.role = 'state-card-illustration';
  if (sceneId) slot.dataset.illustrationId = sceneId;
  // The tinted circle is back, and it is the right frame again. It was dropped
  // while empty states held drawn "scenes" that carried their own frame; now
  // that the artwork is a single large glyph, the circle is what turns it from
  // a stray icon into a deliberate mark.
  slot.className = 'w-24 h-24 mx-auto rounded-full bg-primary-50 dark:bg-primary-900/40 flex items-center justify-center';
  slot.setAttribute('aria-hidden', 'true');
  if (sceneId) {
    slot.append(illustration({ id: sceneId, width: 56, height: 56 }));
  } else if (iconFn) {
    slot.append(iconFn('h-8 w-8'));
  }
  card.append(slot);

  const title = document.createElement('p');
  title.className = 'font-semibold text-ink dark:text-ink-on-dark';
  title.dataset.role = 'state-card-title';
  title.textContent = titleText != null ? titleText : t(titleKey);
  card.append(title);

  const subText = canAct
    ? (subtitleKey ? t(subtitleKey) : null)
    : (memberSubtitle != null ? memberSubtitle : (subtitleKey ? t(subtitleKey) : null));
  if (subText) {
    const sub = document.createElement('p');
    sub.className = 'text-sm text-ink-muted';
    sub.dataset.role = 'state-card-subtitle';
    sub.textContent = subText;
    card.append(sub);
  }

  if (canAct && actions.length > 0) {
    const row = document.createElement('div');
    row.className = 'flex flex-wrap justify-center gap-2';
    for (const a of actions) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = a.primary === false ? 'btn-ghost' : 'btn-primary';
      btn.textContent = t(a.labelKey);
      btn.addEventListener('click', () => a.onSelect());
      row.append(btn);
    }
    card.append(row);
  }

  return card;
}

/**
 * Zero *matches*, not zero *data*. Never offers a create CTA: telling someone
 * who searched 400 products for "Barilla" that their catalogue is empty and
 * inviting them to create their first product reads as data loss.
 *
 * @param {{ titleKey?: string, messageKey?: string, params?: Record<string, unknown>, onClear: () => void, clearLabelKey?: string }} opts
 * @returns {HTMLElement}
 */
export function stateFiltered({
  titleKey,
  messageKey = 'filters.no_matches',
  params = {},
  onClear,
  clearLabelKey = 'filters.clear',
}) {
  const card = shell('filtered');
  card.className = 'card text-center mt-2 space-y-3';

  if (titleKey) {
    const title = document.createElement('p');
    title.className = 'font-semibold';
    title.dataset.role = 'state-card-title';
    title.textContent = t(titleKey);
    card.append(title);
  }

  const msg = document.createElement('p');
  msg.className = 'text-sm text-ink-muted';
  msg.dataset.role = 'state-card-subtitle';
  msg.textContent = t(messageKey, params);
  card.append(msg);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.role = 'state-card-clear';
  btn.className = 'btn-ghost mx-auto';
  btn.textContent = t(clearLabelKey);
  btn.addEventListener('click', () => onClear());
  card.append(btn);

  return card;
}

/**
 * Convenience dispatcher for pages that keep their state in one variable.
 *
 * @param {{ kind: StateKind, [k: string]: any }} opts
 * @returns {HTMLElement}
 */
export function stateCard(opts) {
  switch (opts.kind) {
    case 'loading': return stateLoading(opts);
    case 'error': return stateError(opts);
    case 'empty': return stateEmpty(opts);
    case 'filtered': return stateFiltered(opts);
    default: throw new Error(`unknown state kind: ${opts.kind}`);
  }
}
